import { useEffect } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Copy,
  ExternalLink,
  FolderTree,
  Tag as TagIcon,
} from 'lucide-react';
import type { CreatePostInput } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { PostAuditPanel } from '@/components/posts/post-audit-panel';
import { PostForm } from '@/components/posts/post-form';
import { hasPermission } from '@/lib/permissions';
import {
  postDetailQueryOptions,
  useArchivePost,
  useDuplicatePost,
  usePublishPost,
  useUnarchivePost,
  useUnpublishPost,
  useUpdatePost,
} from '@/lib/posts';
import { trackPostVisit } from '@/lib/recent-posts';
import { settingsListQueryOptions } from '@/lib/settings';
import { useSession } from '@/lib/auth';

export const Route = createFileRoute('/_authenticated/posts/$postId')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(postDetailQueryOptions(params.postId)),
  component: EditPostPage,
});

function EditPostPage() {
  const { postId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const query = useQuery(postDetailQueryOptions(postId));
  const update = useUpdatePost(postId);
  const publish = usePublishPost(postId);
  const unpublish = useUnpublishPost(postId);
  const duplicate = useDuplicatePost();
  const archive = useArchivePost(postId);
  const unarchive = useUnarchivePost(postId);
  const canCreate = hasPermission(user, 'post.create');
  const canUpdate = hasPermission(user, 'post.update');
  const canUpdateOwn = hasPermission(user, 'post.update_own');

  // Compute the public site deep link from the `site.url` setting.
  // Empty / unset / non-string value → null (link is hidden — admin
  // hasn't configured the public origin yet). Slug-only path: the
  // public site routes `/<slug>` directly per current convention; if
  // that changes, this is the single place to update.
  const settingsQuery = useQuery(settingsListQueryOptions());
  const publicSiteUrl = (() => {
    const row = settingsQuery.data?.find((s) => s.key === 'site.url');
    if (!row || typeof row.value !== 'string') return null;
    const base = row.value.trim().replace(/\/$/, '');
    return base || null;
  })();

  // Track this visit in localStorage so the Cmd+K palette can offer
  // it as a "recently viewed" shortcut. Fires once data is loaded;
  // re-fires on tab/window refocus only if the post id changes.
  useEffect(() => {
    if (query.data) {
      trackPostVisit({ id: query.data.id, title: query.data.title, slug: query.data.slug });
    }
  }, [query.data]);

  const handleSubmit = async (data: CreatePostInput) => {
    try {
      await update.mutateAsync(data);
    } catch {
      // surfaced via update.error
    }
  };

  if (query.isLoading) {
    return <div className="p-8 text-center text-sm text-slate-500">กำลังโหลด...</div>;
  }

  if (!query.data) {
    return <div className="p-8 text-center text-sm text-slate-500">ไม่พบบทความ</div>;
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              แก้ไขบทความ
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="font-mono">id: {query.data.id}</span>
              {query.data.readingTimeMinutes > 0 && (
                <>
                  <span>·</span>
                  <span>
                    อ่าน ~{query.data.readingTimeMinutes} นาที · {query.data.wordCount} คำ
                  </span>
                </>
              )}
            </div>
            {/* Assigned categories + tags as clickable filter chips —
                click any to drill into other posts sharing that taxonomy.
                Renders nothing when the post has no categories/tags so
                the header stays compact for the common minimal case. */}
            {(query.data.categories.length > 0 || query.data.tags.length > 0) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {query.data.categories.map((c) => (
                  <Link
                    key={c.id}
                    to="/posts"
                    search={{ categoryId: c.id }}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                    title={`ดูบทความในหมวด ${c.name}`}
                  >
                    <FolderTree className="h-2.5 w-2.5" />
                    {c.name}
                  </Link>
                ))}
                {query.data.tags.map((t) => (
                  <Link
                    key={t.id}
                    to="/posts"
                    search={{ tagId: t.id }}
                    className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50"
                    title={`ดูบทความที่แท็ก ${t.name}`}
                  >
                    <TagIcon className="h-2.5 w-2.5" />
                    {t.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Public-site deep link — only renders when the post is
                actually published AND the admin has filled in
                site.url under /settings. Anchor (not Button) so
                middle-click/right-click work naturally for "open in
                new tab" / "copy link" workflows. */}
            {publicSiteUrl && query.data.status === 'published' && (
              <a
                href={`${publicSiteUrl}/${query.data.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                title="เปิดบทความบนเว็บ public ในแท็บใหม่"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                ดูบน public site
              </a>
            )}
            {canCreate && (
              <Button
                variant="outline"
                size="sm"
                disabled={duplicate.isPending}
                onClick={() =>
                  duplicate.mutate(postId, {
                    onSuccess: (post) => {
                      void navigate({ to: '/posts/$postId', params: { postId: post.id } });
                    },
                  })
                }
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                ทำสำเนา
              </Button>
            )}
            {/* Archive toggle — only when user can update this post.
                Author with `post.update_own` can archive their own;
                broader `post.update` can archive any. Server enforces
                the same check. */}
            {(canUpdate || (canUpdateOwn && query.data.author.id === user?.id)) &&
              (query.data.status === 'archived' ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unarchive.isPending}
                  onClick={() => unarchive.mutate()}
                  title="คืนสถานะเป็น draft"
                >
                  <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />
                  เลิกเก็บถาวร
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate()}
                  title="ซ่อนจาก list (เก็บใน DB; กู้คืนได้)"
                >
                  <Archive className="mr-1.5 h-3.5 w-3.5" />
                  เก็บถาวร
                </Button>
              ))}
          </div>
        </div>
        <PostForm
          mode="edit"
          initial={query.data}
          user={user}
          onSubmit={handleSubmit}
          onPublish={(scheduledAt) => publish.mutate(scheduledAt ? { scheduledAt } : {})}
          onUnpublish={() => unpublish.mutate()}
          isSubmitting={update.isPending}
          isPublishing={publish.isPending}
          isUnpublishing={unpublish.isPending}
          error={update.error}
          publishError={publish.error ?? unpublish.error}
        />
        {hasPermission(user, 'audit.read') && <PostAuditPanel postId={postId} />}
      </div>
    </div>
  );
}
